import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileStack,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileSearch,
  ArrowRight,
  XCircle,
} from "lucide-react";
import { useNeedsReviewDocuments, ScannedDocumentStatus, DOC_TYPES_WITHOUT_ACTION, documentUsesLotId } from "@/hooks/useDocuments";
import { formatDistanceToNow, format } from "date-fns";

interface NeedsReviewQueueProps {
  onDocumentClick: (documentId: string) => void;
  selectedDocumentId?: string | null;
}

export function NeedsReviewQueue({ onDocumentClick, selectedDocumentId }: NeedsReviewQueueProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: documents, isLoading } = useNeedsReviewDocuments();

  // Apply search filter
  const filteredDocuments = documents?.filter((doc) => {
    return doc.original_filename.toLowerCase().includes(searchQuery.toLowerCase());
  }) || [];

  const statusConfig: Record<ScannedDocumentStatus, { label: string; color: string; icon: React.ReactNode }> = {
    detected: { label: "Detected", color: "bg-slate-500", icon: <FileStack className="h-3 w-3" /> },
    parsing: { label: "Parsing", color: "bg-blue-500", icon: <FileSearch className="h-3 w-3" /> },
    parsed: { label: "Parsed", color: "bg-purple-500", icon: <CheckCircle2 className="h-3 w-3" /> },
    needs_review: { label: "Needs Review", color: "bg-amber-500", icon: <AlertCircle className="h-3 w-3" /> },
    routing: { label: "Routing", color: "bg-indigo-500", icon: <ArrowRight className="h-3 w-3" /> },
    routed: { label: "Routed", color: "bg-green-500", icon: <CheckCircle2 className="h-3 w-3" /> },
    failed: { label: "Failed", color: "bg-red-500", icon: <XCircle className="h-3 w-3" /> },
  };

  const getConfidenceBadgeColor = (score: number | null) => {
    if (score === null) return "bg-slate-500";
    if (score >= 0.75) return "bg-green-500";
    if (score >= 0.5) return "bg-amber-500";
    return "bg-red-500";
  };

  const getMissingFields = (doc: typeof filteredDocuments[0]) => {
    const missing: string[] = [];
    const pr = doc.parse_result;
    if (!pr) return ["No parse data"];
    if (!pr.parsed_date) missing.push("Date");
    if (!pr.parsed_client_id && !pr.parsed_client_name) missing.push("Client");
    if (!pr.parsed_lot_id && documentUsesLotId(pr.doc_type, pr.parsed_action)) missing.push("Lot ID");
    if (!pr.doc_type) missing.push("Doc Type");
    if (!pr.parsed_action && !(pr.doc_type && DOC_TYPES_WITHOUT_ACTION.has(pr.doc_type))) missing.push("Action");
    return missing.length > 0 ? missing : ["None"];
  };

  return (
    <div className="space-y-4">
      {/* Header + Search */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Badge variant="outline" className="text-sm">
          {filteredDocuments.length} documents
        </Badge>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileStack className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">No active documents</h3>
          <p className="text-sm text-muted-foreground mt-1">
            All documents have been routed successfully
          </p>
        </div>
      ) : (
        <div className="border rounded-lg bg-white dark:bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Detected</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Missing Fields</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.map((doc) => {
                const config = statusConfig[doc.status];
                const missingFields = getMissingFields(doc);
                const confidence = doc.parse_result?.confidence_score;

                const isSelected = selectedDocumentId === doc.id;

                return (
                  <TableRow
                    key={doc.id}
                    className={`cursor-pointer hover:bg-muted/50 ${isSelected ? "bg-muted/50" : ""}`}
                    onClick={() => onDocumentClick(doc.id)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileStack className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        {/* Always truncate: the shared Table wrapper is overflow-clip (for
                            sticky headers), so an unbounded filename pushes the Status and
                            Review columns off the right edge with no scrollbar. */}
                        <span className="truncate max-w-[200px] md:max-w-[300px] xl:max-w-[420px]" title={doc.original_filename}>
                          {doc.original_filename}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {format(new Date(doc.detected_at), "MMM d, yyyy")}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(doc.detected_at), { addSuffix: true })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {confidence !== null && confidence !== undefined ? (
                        <Badge className={getConfidenceBadgeColor(confidence)}>
                          {Math.round(confidence * 100)}%
                        </Badge>
                      ) : (
                        <Badge variant="outline">N/A</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {missingFields.slice(0, 3).map((field) => (
                          <Badge key={field} variant="outline" className="text-xs">
                            {field}
                          </Badge>
                        ))}
                        {missingFields.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{missingFields.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${config.color} flex items-center gap-1 w-fit whitespace-nowrap`}>
                        {config.icon}
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDocumentClick(doc.id);
                        }}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
